import type { AppConfig, InputDeviceId } from '../app/Config.ts';
import { DELAY_PRESETS, getMode, MODES, type ModeId } from '../app/Modes.ts';
import { DIFFICULTY_PRESETS, maxReachableGapStep } from '../game/Difficulty.ts';
import { flapApexRise, terminalVelocity } from '../game/Physics.ts';
import { button, checkbox, el, numberField, panel, select, slider } from './dom.ts';

export interface ControlPanelCallbacks {
  onModeChange: (mode: ModeId) => void;
  onRestart: () => void;
  onTogglePause: () => void;
  /** A parameter changed; re-apply live config to the running objects. */
  onConfigChanged: () => void;
  onConnectDevice: () => void;
  onConnectStimulator: () => void;
  onExportJSON: () => void;
  onExportCSV: () => void;
  onResetConfig: () => void;
}

/**
 * Mode selection and every live-editable parameter.
 *
 * Grouped to match the mental model rather than the class structure: what is
 * driving the bird, how hard the game is, how the bot thinks, what kind of
 * participant is being simulated, and what the safety layer will allow.
 *
 * Parameters mutate the shared `AppConfig` in place and then call
 * `onConfigChanged`, which is where they are pushed into the live objects.
 * Nothing here reaches into a controller or a stimulator directly.
 */
export class ControlPanel {
  readonly root: HTMLElement;

  private modeDescription: HTMLElement;
  private pauseBtn: HTMLButtonElement;
  private deviceRow: HTMLElement;
  private humanSection: HTMLElement;
  private botSection: HTMLElement;
  private hhiSection: HTMLElement;
  private connectDeviceBtn: HTMLButtonElement;
  private connectStimBtn: HTMLButtonElement;
  private latencySlider: { row: HTMLElement; set: (v: number) => void };
  private gravitySlider!: { row: HTMLElement; set: (v: number) => void };
  private dragSlider!: { row: HTMLElement; set: (v: number) => void };
  private impulseSlider!: { row: HTMLElement; set: (v: number) => void };
  private spacingSlider!: { row: HTMLElement; set: (v: number) => void };
  private gapStepSlider!: { row: HTMLElement; set: (v: number) => void };
  private flightNote!: HTMLElement;
  private gapStepNote!: HTMLElement;
  private jitterSlider: { row: HTMLElement; set: (v: number) => void };
  private botLatencySlider: { row: HTMLElement; set: (v: number) => void };

  constructor(
    private readonly config: AppConfig,
    private readonly cb: ControlPanelCallbacks,
  ) {
    this.root = el('div', 'controls');

    // ---------------------------------------------------------------- mode --
    const modePanel = panel('Mode');
    const modeSelect = select(
      MODES.map((m) => ({ value: m.id, label: m.label })),
      config.mode,
      (v) => cb.onModeChange(v),
    );
    modePanel.body.appendChild(modeSelect);
    this.modeDescription = el('p', 'hint', getMode(config.mode).description);
    modePanel.body.appendChild(this.modeDescription);

    this.deviceRow = el('div', 'device-row');
    modePanel.body.appendChild(this.deviceRow);
    this.connectDeviceBtn = button('Connect input device', cb.onConnectDevice, 'btn-secondary');
    this.connectStimBtn = button('Connect stimulator', cb.onConnectStimulator, 'btn-secondary');
    modePanel.body.append(this.connectDeviceBtn, this.connectStimBtn);

    const actions = el('div', 'button-row');
    this.pauseBtn = button('Pause', cb.onTogglePause);
    actions.append(button('Restart', cb.onRestart), this.pauseBtn);
    modePanel.body.appendChild(actions);

    // ---------------------------------------------------------------- game --
    const gamePanel = panel('Game');

    const presetRow = el('div', 'button-row wrap');
    for (const preset of DIFFICULTY_PRESETS) {
      const b = button(
        `${preset.label} · ${preset.measuredFlapsPerSec.toFixed(2)}/s`,
        () => {
          Object.assign(config.game, preset.config);
          cb.onConfigChanged();
          this.syncGameSliders();
          cb.onRestart();
        },
        'btn-chip',
      );
      b.title = preset.description;
      presetRow.appendChild(b);
    }
    gamePanel.body.append(
      presetRow,
      el(
        'p',
        'hint',
        'Rates are measured, not estimated: flaps per second a direct bot actually needed ' +
          'over 10 seeds × 90 s. In a mediated run that is the stimulation rate.',
      ),
    );

    this.gravitySlider = slider('Gravity', config.game.gravity, 0, 2200, 10, (v) => {
      config.game.gravity = v;
      cb.onConfigChanged();
      this.syncGameSliders();
    }, (v) => `${v} px/s²`);
    this.dragSlider = slider('Drag', config.game.drag, 0, 4, 0.1, (v) => {
      config.game.drag = v;
      cb.onConfigChanged();
      this.syncGameSliders();
    }, (v) => (v === 0 ? 'none' : `${v.toFixed(1)} /s`));
    this.impulseSlider = slider('Flap impulse', -config.game.flapImpulse, 100, 700, 10, (v) => {
      config.game.flapImpulse = -v;
      cb.onConfigChanged();
      this.syncGameSliders();
    }, (v) => `${v} px/s`);
    this.spacingSlider = slider('Pipe spacing', config.game.pipeSpacing, 160, 480, 10, (v) => {
      config.game.pipeSpacing = v;
      cb.onConfigChanged();
      this.syncGameSliders();
    }, (v) => `${v} px`);
    this.gapStepSlider = slider('Max gap step', config.game.maxGapCenterStep, 0, 400, 10, (v) => {
      config.game.maxGapCenterStep = v;
      cb.onConfigChanged();
      this.syncGameSliders();
    }, (v) => (v === 0 ? 'unlimited' : `${v} px`));

    this.flightNote = el('p', 'hint');

    gamePanel.body.append(
      numberField('Seed', config.game.seed, (v) => {
        config.game.seed = Math.floor(v);
        cb.onConfigChanged();
        cb.onRestart();
      }, { min: 0, step: 1, hint: 'Same seed = same pipe course. Restart to apply.' }).row,
      slider('Pipe gap', config.game.pipeGap, 90, 300, 5, (v) => {
        config.game.pipeGap = v;
        cb.onConfigChanged();
      }, (v) => `${v} px`).row,
      slider('Pipe speed', config.game.pipeSpeed, 60, 260, 5, (v) => {
        config.game.pipeSpeed = v;
        cb.onConfigChanged();
        this.syncGameSliders();
      }, (v) => `${v} px/s`).row,
      this.gravitySlider.row,
      this.dragSlider.row,
      this.impulseSlider.row,
      this.spacingSlider.row,
      this.flightNote,
    );

    // ------------------------------------------------------------ forgiveness
    const forgivePanel = panel('Forgiveness (invisible on screen)');
    this.gapStepNote = el('p', 'hint');
    forgivePanel.body.append(
      el(
        'p',
        'hint',
        'These make the game survivable without changing a pixel. They buy tolerance, ' +
          'not quiet — the flap rate is set by the flight model above.',
      ),
      this.gapStepSlider.row,
      this.gapStepNote,
      slider('Hitbox size', config.game.hitboxScale, 0.4, 1, 0.05, (v) => {
        config.game.hitboxScale = v;
        cb.onConfigChanged();
      }, (v) => `${(v * 100).toFixed(0)}% of the body`).row,
      slider('Collision grace', config.game.collisionGraceMs, 0, 250, 10, (v) => {
        config.game.collisionGraceMs = v;
        cb.onConfigChanged();
      }, (v) => (v === 0 ? 'none' : `${v} ms`)).row,
      el(
        'p',
        'hint',
        'Collision grace is the one that forgives a *late* participant rather than a badly ' +
          'aimed one: a flap arriving within the window still rescues a clip. Pipes only — ' +
          'the ground is always fatal.',
      ),
    );

    // ------------------------------------------------------------------ look
    const lookPanel = panel('Look');
    lookPanel.body.append(
      (() => {
        const row = el('div', 'device-row');
        row.append(
          el('span', 'device-label', 'Style'),
          select(
            [
              { value: 'arcade' as const, label: '8-bit arcade' },
              { value: 'lab' as const, label: 'Lab (flat, high contrast)' },
            ],
            config.renderTheme,
            (v) => {
              config.renderTheme = v;
              cb.onConfigChanged();
            },
          ),
        );
        return row;
      })(),
      checkbox('Show what the bot sees', config.showBotVision, (v) => {
        config.showBotVision = v;
        cb.onConfigChanged();
      }),
      el(
        'p',
        'hint',
        'Lab look drops the scenery for maximum overlay contrast — use it for figures.',
      ),
    );

    // ----------------------------------------------------------------- bot --
    const botPanel = panel('Bot');
    this.botSection = botPanel.body;
    this.botLatencySlider = slider(
      'Assumed actuator latency', config.bot.latencyMs, 0, 400, 5,
      (v) => { config.bot.latencyMs = v; cb.onConfigChanged(); },
      (v) => `${v} ms`,
    );
    botPanel.body.append(
      checkbox('Latency compensation', config.bot.latencyCompensation, (v) => {
        config.bot.latencyCompensation = v;
        cb.onConfigChanged();
      }),
      this.botLatencySlider.row,
      slider('In-flight grace', config.bot.inFlightGraceMs, 0, 200, 5, (v) => {
        config.bot.inFlightGraceMs = v;
        cb.onConfigChanged();
      }, (v) => `${v} ms`).row,
      slider('Min interval between intents', config.bot.minIntentIntervalMs, 20, 500, 10, (v) => {
        config.bot.minIntentIntervalMs = v;
        cb.onConfigChanged();
      }, (v) => `${v} ms`).row,
      slider('Aim below gap centre', config.bot.aimFraction, -0.5, 1.2, 0.05, (v) => {
        config.bot.aimFraction = v;
        cb.onConfigChanged();
      }, (v) => `${v.toFixed(2)} × flap arc`).row,
      slider('Dead band', config.bot.deadband, 0, 40, 1, (v) => {
        config.bot.deadband = v;
        cb.onConfigChanged();
      }, (v) => `${v} px`).row,
      el(
        'p',
        'hint',
        'The assumed latency is what the bot plans against — it is a belief, not ground ' +
          'truth. Set it correctly and 150 ms of delay is survivable; turn compensation ' +
          'off and the same bird dies in seconds.',
      ),
    );

    // ------------------------------------------------------- simulated human --
    const humanPanel = panel('Simulated participant');
    this.humanSection = humanPanel.body;

    const presets = el('div', 'button-row wrap');
    for (const p of DELAY_PRESETS) {
      presets.appendChild(
        button(p.label, () => {
          config.human.meanLatencyMs = p.meanLatencyMs;
          config.human.latencyStdDevMs = p.latencyStdDevMs;
          // A preset describes an actuator the bot is calibrated for, so it
          // moves the bot's estimate with it. Un-calibrate deliberately with
          // the latency slider or the compensation switch, not by accident.
          config.bot.latencyMs = p.meanLatencyMs;
          this.latencySlider.set(p.meanLatencyMs);
          this.jitterSlider.set(p.latencyStdDevMs);
          this.botLatencySlider.set(p.meanLatencyMs);
          cb.onConfigChanged();
        }, 'btn-chip'),
      );
    }
    this.latencySlider = slider(
      'Mean response latency', config.human.meanLatencyMs, 0, 400, 5,
      (v) => { config.human.meanLatencyMs = v; cb.onConfigChanged(); },
      (v) => `${v} ms`,
    );
    this.jitterSlider = slider(
      'Latency jitter (σ)', config.human.latencyStdDevMs, 0, 100, 1,
      (v) => { config.human.latencyStdDevMs = v; cb.onConfigChanged(); },
      (v) => `${v} ms`,
    );
    humanPanel.body.append(
      presets,
      this.latencySlider.row,
      this.jitterSlider.row,
      slider('Success probability', config.human.successProbability, 0, 1, 0.01, (v) => {
        config.human.successProbability = v;
        cb.onConfigChanged();
      }, (v) => `${(v * 100).toFixed(0)}%`).row,
      slider('False positives', config.human.falsePositiveProbability, 0, 3, 0.05, (v) => {
        config.human.falsePositiveProbability = v;
        cb.onConfigChanged();
      }, (v) => `${v.toFixed(2)}/s`).row,
      slider('Participant refractory', config.human.refractoryPeriodMs, 0, 500, 10, (v) => {
        config.human.refractoryPeriodMs = v;
        cb.onConfigChanged();
      }, (v) => `${v} ms`).row,
      slider('Contact hold time', config.human.buttonHoldMs, 10, 200, 5, (v) => {
        config.human.buttonHoldMs = v;
        cb.onConfigChanged();
      }, (v) => `${v} ms`).row,
    );

    // -------------------------------------------------------------- safety --
    const safetyPanel = panel('Safety limits', 'panel-safety');
    safetyPanel.body.append(
      el(
        'p',
        'hint',
        'The software layer refuses any request that breaks these. They are a backstop ' +
          'against a controller bug, not a substitute for the hardware’s own limits.',
      ),
      numberField('Minimum interval between stimuli', config.safety.minIntervalMs, (v) => {
        config.safety.minIntervalMs = v;
        cb.onConfigChanged();
      }, { min: 0, step: 10, unit: 'ms' }).row,
      numberField('Max stimuli per second', config.safety.maxPerSecond, (v) => {
        config.safety.maxPerSecond = v;
        cb.onConfigChanged();
      }, { min: 1, step: 1 }).row,
      numberField('Max stimuli per minute', config.safety.maxPerMinute, (v) => {
        config.safety.maxPerMinute = v;
        cb.onConfigChanged();
      }, { min: 1, step: 10 }).row,
      numberField('Max pulse duration', config.safety.maxDurationMs, (v) => {
        config.safety.maxDurationMs = v;
        cb.onConfigChanged();
      }, { min: 1, step: 10, unit: 'ms' }).row,
      numberField('Max stimuli per session', config.safety.maxSessionStimuli, (v) => {
        config.safety.maxSessionStimuli = v;
        cb.onConfigChanged();
      }, { min: 1, step: 50 }).row,
      numberField('Requested pulse duration', config.stimController.stimulusDurationMs, (v) => {
        config.stimController.stimulusDurationMs = v;
        cb.onConfigChanged();
      }, { min: 1, step: 5, unit: 'ms' }).row,
      checkbox(
        'Apply these limits in simulation too',
        config.enforceSafetyLimitsInSimulation,
        (v) => {
          config.enforceSafetyLimitsInSimulation = v;
          cb.onConfigChanged();
        },
      ),
      el(
        'p',
        'hint',
        'Off by default: with a simulated participant these limits throttle a synthetic ' +
          'actuator and hide the response model being measured. Turn them on to see what ' +
          'the rate limiter will cost a real participant. Real hardware always gets them.',
      ),
    );

    // ----------------------------------------------------------------- HHI --
    const hhiPanel = panel('Backyard Brains HHI');
    this.hhiSection = hhiPanel.root;
    hhiPanel.body.append(
      el(
        'p',
        'hint',
        'Connect the controller, then use START GAME at the top. The bot sends each impulse; ' +
          'the participant’s Space press moves the bird. Escape stops immediately.',
      ),
    );

    // -------------------------------------------------------------- session --
    const sessionPanel = panel('Session');
    sessionPanel.body.append(
      checkbox('Log full game state on every event', config.logGameState, (v) => {
        config.logGameState = v;
        cb.onConfigChanged();
      }),
      (() => {
        const row = el('div', 'button-row');
        row.append(
          button('Export JSON', cb.onExportJSON),
          button('Export CSV', cb.onExportCSV),
          button('Reset settings', cb.onResetConfig, 'btn-secondary'),
        );
        return row;
      })(),
    );

    this.root.append(
      modePanel.root, gamePanel.root, forgivePanel.root, lookPanel.root, botPanel.root,
      humanPanel.root, safetyPanel.root, hhiPanel.root, sessionPanel.root,
    );

    this.syncGameSliders();
    this.refresh();
  }

  /** Show only the sections that apply to the current mode. */
  refresh(): void {
    const mode = getMode(this.config.mode);
    this.modeDescription.textContent = mode.description;
    this.botSection.parentElement!.hidden = !mode.usesBot;
    this.humanSection.parentElement!.hidden = this.config.mode !== 'simulated-human';
    this.hhiSection.hidden = this.config.mode !== 'hhi-human';
    this.connectDeviceBtn.hidden = !mode.requiresHardware;
    this.connectStimBtn.hidden = !mode.usesStimulation;
    this.botLatencySlider.set(this.config.bot.latencyMs);

    this.deviceRow.replaceChildren();
    if (this.config.mode === 'physical-button' || this.config.mode === 'hhi-human') {
      this.deviceRow.append(
        el('span', 'device-label', 'Input device'),
        select<InputDeviceId>(
          [
            { value: 'keyboard-key', label: `Keyboard "${this.config.keyboardButtonKey}" (dev stand-in)` },
            { value: 'serial', label: 'Serial (WebSerial)' },
            { value: 'websocket', label: `WebSocket (${this.config.websocketUrl})` },
          ],
          this.config.buttonDevice === 'simulated-emg' ? 'keyboard-key' : this.config.buttonDevice,
          (v) => {
            this.config.buttonDevice = v;
            this.cb.onModeChange(this.config.mode);
          },
        ),
      );
    } else if (this.config.mode === 'emg') {
      this.deviceRow.append(
        el('span', 'device-label', 'EMG source'),
        select<InputDeviceId>(
          [
            { value: 'simulated-emg', label: 'Simulated EMG (press B to contract)' },
            { value: 'serial', label: 'Serial (WebSerial)' },
            { value: 'websocket', label: `WebSocket (${this.config.websocketUrl})` },
          ],
          this.config.emgDevice,
          (v) => {
            this.config.emgDevice = v;
            this.cb.onModeChange(this.config.mode);
          },
        ),
      );
    }
  }

  /**
   * Push slider positions back from the config (a preset may have moved them)
   * and refresh the two derived readouts.
   *
   * The readouts matter more than they look. Sink rate and flap arc are what
   * actually determine how often a participant gets stimulated, and the
   * reachability figure is the difference between a hard course and an
   * impossible one — a gap step larger than the bird can descend in the time
   * available cannot be flown by any controller, human or otherwise.
   */
  syncGameSliders(): void {
    const g = this.config.game;
    this.gravitySlider.set(g.gravity);
    this.dragSlider.set(g.drag);
    this.impulseSlider.set(-g.flapImpulse);
    this.spacingSlider.set(g.pipeSpacing);
    this.gapStepSlider.set(g.maxGapCenterStep);

    const sink = terminalVelocity(g);
    const rise = flapApexRise(g);
    const secondsBetween = g.pipeSpacing / Math.max(1, g.pipeSpeed);

    if (g.gravity <= 0) {
      this.flightNote.textContent =
        'Gravity is zero: nothing pulls the bird down, so it can only ever climb and any ' +
        'gap below it is unreachable. Useful as a limit case; not a playable course.';
      this.flightNote.className = 'hint tone-warn';
    } else if (g.drag <= 0) {
      this.flightNote.textContent =
        `No drag: the bird accelerates to ${sink.toFixed(0)} px/s rather than settling. ` +
        `Flap arc ${rise.toFixed(0)} px. Add drag to make low gravity worth anything.`;
      this.flightNote.className = 'hint';
    } else {
      this.flightNote.textContent =
        `Sink rate ${sink.toFixed(0)} px/s · flap arc ${rise.toFixed(0)} px · ` +
        `${secondsBetween.toFixed(2)} s between pipes.`;
      this.flightNote.className = 'hint';
    }

    const reachable = maxReachableGapStep(g);
    const step = g.maxGapCenterStep > 0 ? g.maxGapCenterStep : Infinity;
    const span = g.height - 2 * g.gapMargin - g.pipeGap;
    const effective = Math.min(step, span);
    if (effective > reachable) {
      this.gapStepNote.textContent =
        `Gaps can step ${effective.toFixed(0)} px but the bird can only descend about ` +
        `${reachable.toFixed(0)} px between pipes. Some courses will be unflyable.`;
      this.gapStepNote.className = 'hint tone-bad';
    } else {
      this.gapStepNote.textContent =
        `Reachable: the bird can descend about ${reachable.toFixed(0)} px between pipes.`;
      this.gapStepNote.className = 'hint';
    }
  }

  setPaused(paused: boolean): void {
    this.pauseBtn.textContent = paused ? 'Resume' : 'Pause';
  }
}
